import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ChevronDown, ChevronUp, Banknote, Copy, Home, CreditCard, Car, Package, Repeat, HelpCircle, Info } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import MoneyMovementFields from "../components/MoneyMovementFields";
import ConceptSelect from "../components/ConceptSelect";
import MemberSelect, { MemberBadge } from "../components/MemberSelect";
import MovementTrace from "../components/MovementTrace";
import ProviderSelect from "../components/ProviderSelect";

// ---------------------------------------------------------------------------
// Reusable label with hint (i)
// ---------------------------------------------------------------------------
function LabelHint({ label, hint, htmlFor, testid }) {
  return (
    <label htmlFor={htmlFor} className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
      <span>{label}</span>
      {hint && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid={testid || `hint-${label}`} className="text-muted-foreground hover:text-foreground focus:outline-none" aria-label={`Info: ${label}`}>
              <Info size={12} />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="text-xs w-64 leading-relaxed">
            {hint}
          </PopoverContent>
        </Popover>
      )}
    </label>
  );
}

const num = (v, d = 0) => parseFloat(String(v).replace(",", ".")) || d;

// -----------------------------------------------------------------------------
// Tipología de obligaciones
// -----------------------------------------------------------------------------
const OBLIGATION_TYPES = [
  { code: "hipoteca", label: "Hipoteca", icon: Home, group: "debt" },
  { code: "prestamo", label: "Préstamo", icon: Banknote, group: "debt" },
  { code: "credito", label: "Crédito personal", icon: CreditCard, group: "debt" },
  { code: "tarjeta", label: "Tarjeta", icon: CreditCard, group: "debt" },
  { code: "leasing", label: "Leasing", icon: Car, group: "lease" },
  { code: "renting", label: "Renting", icon: Car, group: "lease" },
  { code: "alquiler_devolucion", label: "Alquiler con opción de devolución", icon: Car, group: "lease" },
  { code: "suscripcion_bien", label: "Suscripción de bienes (Grover…)", icon: Package, group: "subscription" },
  { code: "suscripcion_financiera", label: "Suscripción financiera", icon: Repeat, group: "subscription" },
  { code: "otros", label: "Otros compromisos", icon: HelpCircle, group: "debt" },
];

const TYPE_META = Object.fromEntries(OBLIGATION_TYPES.map((t) => [t.code, t]));

const groupOf = (code) => TYPE_META[code]?.group || "debt";

const EMPTY = {
  obligation_type: "prestamo",
  name: "", entity: "", provider_id: null, concept_id: null, member_id: null, start_date: "",
  original_amount: "", num_installments: "", monthly_payment: "", interest_rate: "",
  rate_type: "Fijo", tae: "", opening_fee: "", manual_cost: "", modification_fee: "",
  late_interest_rate: "", max_late_months: "", early_repayment_pct: "1", early_reduced_pct: "0.5", early_reduced_after_months: "12",
  status: "activo", account_id: null, payment_method_id: null,
  // leasing/renting
  asset_current_value: "", residual_value: "", exit_cost: "", add_to_patrimonio: false, linked_asset_id: null,
  // suscripción
  end_date: "", total_paid_manual: "", can_return: true, has_buy_option: false, buy_option_price: "",
};

// -----------------------------------------------------------------------------
// Card
// -----------------------------------------------------------------------------
function ObligationCard({ f, onEdit, onDelete, onDuplicate }) {
  const [detail, setDetail] = useState(false);
  const [view, setView] = useState("financiera");
  const group = groupOf(f.obligation_type);
  const meta = TYPE_META[f.obligation_type] || {};
  const Icon = meta.icon || Banknote;

  const hasProgress = group !== "subscription" || (f.end_date && f.num_installments);
  const pct = (() => {
    if (group === "subscription") {
      const total = (f.paid_installments || 0) + (f.remaining_installments || 0);
      return total > 0 ? Math.round((f.paid_installments / total) * 100) : 0;
    }
    return f.num_installments ? Math.round((f.paid_installments / f.num_installments) * 100) : 0;
  })();

  return (
    <div className="border border-border rounded-lg p-5 space-y-3 hover:border-foreground/30 transition-colors" data-testid={`financing-card-${f.id}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-heading font-bold flex items-center gap-2">
            <Icon size={16} className="text-muted-foreground shrink-0" />
            <span className="truncate">{f.name}</span>
            <MemberBadge memberId={f.member_id} />
            {f.status && f.status !== "activo" && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${f.status === "pausado" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{f.status}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {meta.label || f.obligation_type} · {f.provider_name || f.entity || "—"}
            {group === "debt" && f.interest_rate ? ` · ${f.interest_rate}% TIN` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid={`duplicate-financing-${f.id}`} onClick={onDuplicate} title="Duplicar" className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
          <button data-testid={`edit-financing-${f.id}`} onClick={onEdit} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
          <button data-testid={`delete-financing-${f.id}`} onClick={onDelete} className="text-muted-foreground hover:text-red-600"><Trash2 size={15} /></button>
        </div>
      </div>

      {hasProgress && (
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{f.paid_installments} de {(f.remaining_installments || 0) + (f.paid_installments || 0)} {group === "subscription" ? "meses" : "cuotas"}</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-foreground rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* Selector vista Financiera / Práctica */}
      {group !== "debt" || f.obligation_type !== "prestamo" ? (
        <div className="flex gap-1 border-b border-border" data-testid={`view-toggle-${f.id}`}>
          <button onClick={() => setView("financiera")} className={`px-3 py-1 -mb-px text-xs font-medium border-b-2 ${view === "financiera" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>Vista financiera</button>
          <button onClick={() => setView("practica")} className={`px-3 py-1 -mb-px text-xs font-medium border-b-2 ${view === "practica" ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>Vista práctica</button>
        </div>
      ) : null}

      {view === "financiera" ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div><div className="text-xs text-muted-foreground">Cuota mensual</div><Money value={f.monthly_payment} className="font-semibold tabular" /></div>
          {group === "subscription" ? (
            <>
              <div><div className="text-xs text-muted-foreground">Coste anual estimado</div><Money value={f.annual_cost_estimate} className="font-semibold tabular" /></div>
              <div><div className="text-xs text-muted-foreground">Total pagado</div><Money value={f.total_paid} className="font-semibold tabular" /></div>
              <div><div className="text-xs text-muted-foreground">Finaliza</div><span className="font-semibold">{f.end_date ? fmtDate(f.end_date) : "—"}</span></div>
            </>
          ) : (
            <>
              <div><div className="text-xs text-muted-foreground">Pendiente</div><Money value={f.pending_amount} className="font-semibold tabular text-red-600" /></div>
              <div><div className="text-xs text-muted-foreground">Liquidación hoy</div><Money value={f.liquidation_estimate} className="font-semibold tabular" /></div>
              <div><div className="text-xs text-muted-foreground">Finaliza</div><span className="font-semibold">{fmtDate(f.end_date)}</span></div>
            </>
          )}
        </div>
      ) : (
        <PracticalView f={f} group={group} />
      )}

      {f.active && f.next_due_date && (
        <div className="text-xs text-muted-foreground border-t border-border pt-2">Próxima cuota: <span className="font-semibold text-foreground">{fmtDate(f.next_due_date)}</span></div>
      )}

      <button data-testid={`toggle-detail-${f.id}`} onClick={() => setDetail(!detail)} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
        {detail ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Desglose completo
      </button>
      {detail && group !== "subscription" && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t border-border pt-3" data-testid={`detail-${f.id}`}>
          <div><div className="text-xs text-muted-foreground">Capital prestado</div><Money value={f.original_amount} className="font-semibold tabular" /></div>
          <div><div className="text-xs text-muted-foreground">Intereses totales</div><Money value={f.total_interest} className="font-semibold tabular" /></div>
          <div><div className="text-xs text-muted-foreground">Coste desembolso</div><Money value={f.disbursement_cost} className="font-semibold tabular" /></div>
          <div><div className="text-xs text-muted-foreground">Coste total operación</div><Money value={f.total_operation_cost} className="font-semibold tabular" /></div>
          <div><div className="text-xs text-muted-foreground">Tipo deudor</div><span className="font-semibold">{f.rate_type}</span></div>
          <div><div className="text-xs text-muted-foreground">TAE</div><span className="font-semibold tabular">{f.tae} %</span></div>
        </div>
      )}
      {detail && group === "subscription" && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t border-border pt-3" data-testid={`detail-${f.id}`}>
          <div><div className="text-xs text-muted-foreground">Inicio</div><span className="font-semibold">{fmtDate(f.start_date)}</span></div>
          <div><div className="text-xs text-muted-foreground">Fin</div><span className="font-semibold">{f.end_date ? fmtDate(f.end_date) : "—"}</span></div>
          <div><div className="text-xs text-muted-foreground">Puede devolverse</div><span className="font-semibold">{f.can_return ? "Sí" : "No"}</span></div>
          <div><div className="text-xs text-muted-foreground">Opción de compra</div><span className="font-semibold">{f.has_buy_option ? <Money value={f.buy_option_price} className="tabular" /> : "No"}</span></div>
        </div>
      )}
    </div>
  );
}

function PracticalView({ f, group }) {
  if (group === "lease") {
    const exp = f.exposure_real ?? 0;
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div className="col-span-2 rounded-md bg-secondary/40 p-3">
          <div className="text-xs text-muted-foreground">Si devuelves el activo hoy…</div>
          {exp <= 0 ? (
            <div className="font-heading font-bold text-emerald-600" data-testid={`exposure-${f.id}`}>
              Exceso favorable: +<Money value={Math.abs(exp)} className="tabular" />
            </div>
          ) : (
            <div className="font-heading font-bold text-red-600" data-testid={`exposure-${f.id}`}>
              Déficit estimado: <Money value={exp} className="tabular" />
            </div>
          )}
        </div>
        <div><div className="text-xs text-muted-foreground">Deuda pendiente</div><Money value={f.pending_amount} className="font-semibold tabular text-red-600" /></div>
        <div><div className="text-xs text-muted-foreground">Valor actual del activo</div><Money value={f.asset_current_value} className="font-semibold tabular text-emerald-600" /></div>
        <div><div className="text-xs text-muted-foreground">Valor residual</div><Money value={f.residual_value} className="font-semibold tabular" /></div>
        <div><div className="text-xs text-muted-foreground">Coste de salida</div><Money value={f.exit_cost} className="font-semibold tabular" /></div>
      </div>
    );
  }
  if (group === "subscription") {
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div className="col-span-2 rounded-md bg-secondary/40 p-3">
          <div className="text-xs text-muted-foreground">Si cancelas / devuelves hoy…</div>
          <div className="font-heading font-bold" data-testid={`practical-${f.id}`}>
            {f.can_return ? "Sin coste de devolución" : "Consulta condiciones del proveedor"}
          </div>
        </div>
        <div><div className="text-xs text-muted-foreground">Cuota mensual</div><Money value={f.monthly_payment} className="font-semibold tabular" /></div>
        <div><div className="text-xs text-muted-foreground">Coste anual</div><Money value={f.annual_cost_estimate} className="font-semibold tabular" /></div>
        <div><div className="text-xs text-muted-foreground">Total ya pagado</div><Money value={f.total_paid} className="font-semibold tabular" /></div>
        {f.has_buy_option && (
          <div><div className="text-xs text-muted-foreground">Opción de compra</div><Money value={f.buy_option_price} className="font-semibold tabular" /></div>
        )}
      </div>
    );
  }
  // Deuda tradicional
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      <div className="col-span-2 rounded-md bg-secondary/40 p-3">
        <div className="text-xs text-muted-foreground">Si cancelo la financiación hoy…</div>
        <div className="font-heading font-bold text-red-600">
          Pagaría: <Money value={f.early_repayment_total} className="tabular" />
        </div>
      </div>
      <div><div className="text-xs text-muted-foreground">Capital pendiente</div><Money value={f.liquidation_estimate} className="font-semibold tabular" /></div>
      <div><div className="text-xs text-muted-foreground">Compensación</div><Money value={f.early_repayment_fee} className="font-semibold tabular" /></div>

      {/* Posición neta del activo vinculado */}
      {f.linked_asset_id && f.linked_asset_value != null && (
        <div className="col-span-2 rounded-md border border-border p-3 space-y-1" data-testid={`equity-${f.id}`}>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Posición neta del activo</div>
          <div className="text-xs text-muted-foreground">Vinculado a: <span className="font-semibold text-foreground">{f.linked_asset_name}</span></div>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div><div className="text-xs text-muted-foreground">Valor activo</div><Money value={f.linked_asset_value} className="font-semibold tabular text-emerald-600" /></div>
            <div><div className="text-xs text-muted-foreground">Liquidación actual</div><Money value={f.liquidation_estimate} className="font-semibold tabular text-red-600" /></div>
          </div>
          <div className="mt-1 pt-1 border-t border-border">
            <div className="text-xs text-muted-foreground">Posición neta</div>
            <div className={`font-heading font-bold ${(f.equity_asset ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              <Money value={f.equity_asset} className="tabular" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Página principal
// -----------------------------------------------------------------------------
export default function Financiaciones() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [adv, setAdv] = useState(false);
  const [selected, setSelected] = useState({});
  const [accounts, setAccounts] = useState([]);

  const load = () => api.get("/financings").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/assets").then((r) => setAccounts(r.data));
  }, []);

  const submit = async () => {
    try {
      const g = groupOf(form.obligation_type);
      const payload = {
        obligation_type: form.obligation_type,
        name: form.name,
        concept_id: form.concept_id || null,
        member_id: form.member_id || null,
        entity: form.entity,
        provider_id: form.provider_id || null,
        start_date: form.start_date,
        status: form.status || "activo",
        account_id: form.account_id,
        payment_method_id: form.payment_method_id,
        monthly_payment: num(form.monthly_payment),
        // Deuda tradicional + leasing/renting
        original_amount: num(form.original_amount),
        num_installments: parseInt(form.num_installments) || 0,
        interest_rate: num(form.interest_rate),
        rate_type: form.rate_type || "Fijo",
        tae: num(form.tae),
        opening_fee: num(form.opening_fee),
        manual_cost: num(form.manual_cost),
        modification_fee: num(form.modification_fee),
        late_interest_rate: num(form.late_interest_rate),
        max_late_months: parseInt(form.max_late_months) || 0,
        early_repayment_pct: num(form.early_repayment_pct, 1),
        early_reduced_pct: num(form.early_reduced_pct, 0.5),
        early_reduced_after_months: parseInt(form.early_reduced_after_months) || 12,
      };
      if (g === "lease") {
        payload.asset_current_value = num(form.asset_current_value);
        payload.residual_value = num(form.residual_value);
        payload.exit_cost = num(form.exit_cost);
        if (form.add_to_patrimonio) {
          payload.linked_asset_id = form.linked_asset_id || "__new__";
        } else {
          payload.linked_asset_id = null;
        }
      } else if (g === "debt") {
        // Deuda tradicional: se puede referenciar un activo ya existente (opcional)
        payload.linked_asset_id = form.linked_asset_id || null;
      }
      if (g === "subscription") {
        payload.end_date = form.end_date || null;
        payload.total_paid_manual = form.total_paid_manual === "" ? null : num(form.total_paid_manual);
        payload.can_return = !!form.can_return;
        payload.has_buy_option = !!form.has_buy_option;
        payload.buy_option_price = num(form.buy_option_price);
        // Suscripciones no tienen "num_installments" fijo
        payload.num_installments = 0;
        payload.original_amount = 0;
      }
      if (editId) await api.put(`/financings/${editId}`, payload);
      else await api.post("/financings", payload);
      toast.success(editId ? "Obligación actualizada" : "Obligación creada");
      setOpen(false); setForm(EMPTY); setEditId(null); setAdv(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta obligación?")) return;
    await api.delete(`/financings/${id}`); load();
  };

  const toForm = (f) => ({
    ...EMPTY,
    obligation_type: f.obligation_type || "prestamo",
    name: f.name || "", entity: f.entity || "", provider_id: f.provider_id || null, concept_id: f.concept_id || null, member_id: f.member_id || null, start_date: f.start_date || "",
    original_amount: f.original_amount ?? "", num_installments: f.num_installments ?? "",
    monthly_payment: f.monthly_payment ?? "", interest_rate: f.interest_rate ?? "",
    rate_type: f.rate_type ?? "Fijo", tae: f.tae ?? "",
    opening_fee: f.opening_fee ?? "", manual_cost: f.manual_cost ?? "",
    modification_fee: f.modification_fee ?? "", late_interest_rate: f.late_interest_rate ?? "",
    max_late_months: f.max_late_months ?? "", early_repayment_pct: f.early_repayment_pct ?? "1",
    early_reduced_pct: f.early_reduced_pct ?? "0.5", early_reduced_after_months: f.early_reduced_after_months ?? "12",
    status: f.status || "activo", account_id: f.account_id || null, payment_method_id: f.payment_method_id || null,
    asset_current_value: f.asset_current_value ?? "", residual_value: f.residual_value ?? "",
    exit_cost: f.exit_cost ?? "", add_to_patrimonio: !!f.linked_asset_id, linked_asset_id: f.linked_asset_id || null,
    end_date: f.end_date || "", total_paid_manual: f.total_paid_manual ?? "",
    can_return: f.can_return !== undefined ? !!f.can_return : true,
    has_buy_option: !!f.has_buy_option, buy_option_price: f.buy_option_price ?? "",
  });

  const startEdit = (f) => { setEditDoc(f); setForm(toForm(f)); setEditId(f.id); setOpen(true); };
  const duplicateItem = (f) => { setForm({ ...toForm(f), name: `${f.name} (copia)`, linked_asset_id: null, add_to_patrimonio: false }); setEditId(null); setOpen(true); toast.info("Datos copiados"); };

  const active = (items || []).filter((f) => f.active);
  const finished = (items || []).filter((f) => !f.active);
  const debtActive = active.filter((f) => groupOf(f.obligation_type) === "debt");
  const totalPending = active.reduce((s, f) => s + (f.pending_amount || 0), 0);
  const totalMonthly = active.reduce((s, f) => s + (f.monthly_payment || 0), 0);
  const totalExposure = active.reduce((s, f) => {
    const g = groupOf(f.obligation_type);
    if (g === "subscription") return s;
    if (g === "lease") return s + Math.max(0, f.exposure_real ?? 0);
    return s + (f.pending_amount || 0);
  }, 0);
  const sel = debtActive.filter((f) => selected[f.id]);
  const selCapital = sel.reduce((s, f) => s + (f.liquidation_estimate || 0), 0);
  const selFee = sel.reduce((s, f) => s + (f.early_repayment_fee || 0), 0);

  const g = groupOf(form.obligation_type);

  return (
    <div className="space-y-6" data-testid="financiaciones-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Obligaciones</h1>
          <p className="text-sm text-muted-foreground mt-1">Hipotecas, préstamos, leasing, renting, suscripciones (Grover…). Deuda y exposición real diferenciadas.</p>
        </div>
        <Button data-testid="add-financing-button" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}>
          <Plus size={16} className="mr-1" /> Añadir obligación
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Deuda pendiente</div>
          <Money value={totalPending} testId="total-pendiente" className="block mt-1 text-2xl sm:text-3xl font-semibold tabular text-red-600" />
          <div className="text-[11px] text-muted-foreground mt-1">Suma bruta (todos los tipos)</div>
        </div>
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Exposición real</div>
          <Money value={totalExposure} testId="total-exposure" className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
          <div className="text-[11px] text-muted-foreground mt-1">Descuenta valor de activos (leasing / renting)</div>
        </div>
        <div className="border border-border rounded-lg p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Cuotas al mes</div>
          <Money value={totalMonthly} testId="total-cuotas-mes" className="block mt-1 text-2xl sm:text-3xl font-semibold tabular" />
        </div>
      </div>

      {/* Amortización anticipada — sólo aplica a deuda tradicional */}
      {debtActive.length > 0 && (
        <div className="border border-border rounded-lg p-5" data-testid="amortizacion-section">
          <div className="flex items-center gap-2">
            <Banknote size={18} />
            <h3 className="font-heading font-bold text-lg">Amortización anticipada</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Marca las financiaciones tradicionales que quieres cancelar hoy. La compensación se calcula según regla bancaria.</p>
          <div className="divide-y divide-border">
            {debtActive.map((f) => (
              <label key={f.id} className="flex items-center gap-3 py-2.5 cursor-pointer">
                <Checkbox data-testid={`amort-check-${f.id}`} checked={!!selected[f.id]} onCheckedChange={(v) => setSelected({ ...selected, [f.id]: !!v })} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{f.name}</div>
                  <div className="text-xs text-muted-foreground">Compensación {f.early_pct_applied} % · quedan {f.remaining_installments} cuotas</div>
                </div>
                <div className="text-right">
                  <Money value={f.liquidation_estimate} className="block text-sm font-semibold tabular" />
                  <span className="text-xs text-muted-foreground">+ <Money value={f.early_repayment_fee} className="tabular" /></span>
                </div>
              </label>
            ))}
          </div>
          {sel.length > 0 && (
            <div className="mt-3 rounded-lg bg-primary text-primary-foreground p-4 flex flex-wrap items-end justify-between gap-4" data-testid="amort-summary">
              <div className="flex gap-6 flex-wrap">
                <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Capital ({sel.length})</div><Money value={selCapital} testId="amort-capital" className="text-lg font-semibold tabular" /></div>
                <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Compensación</div><Money value={selFee} testId="amort-fee" className="text-lg font-semibold tabular" /></div>
              </div>
              <div><div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Total a pagar hoy</div><Money value={selCapital + selFee} testId="amort-total" className="text-2xl font-semibold tabular" /></div>
            </div>
          )}
        </div>
      )}

      {items === null ? (
        <div className="text-sm text-muted-foreground">Cargando…</div>
      ) : (
        <>
          <div>
            <h3 className="font-heading font-bold text-lg mb-3">Activas ({active.length})</h3>
            {active.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-border rounded-lg p-8 text-center">Sin obligaciones activas.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {active.map((f) => <ObligationCard key={f.id} f={f} onEdit={() => startEdit(f)} onDelete={() => remove(f.id)} onDuplicate={() => duplicateItem(f)} />)}
              </div>
            )}
          </div>
          {finished.length > 0 && (
            <div>
              <h3 className="font-heading font-bold text-lg mb-3 text-muted-foreground">Finalizadas o pausadas ({finished.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60">
                {finished.map((f) => <ObligationCard key={f.id} f={f} onEdit={() => startEdit(f)} onDelete={() => remove(f.id)} onDuplicate={() => duplicateItem(f)} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* -------- Modal formulario dinámico -------- */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAdv(false); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar obligación" : "Nueva obligación"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Tipo de obligación</label>
              <Select value={form.obligation_type} onValueChange={(v) => setForm({ ...form, obligation_type: v })}>
                <SelectTrigger data-testid="obligation-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OBLIGATION_TYPES.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Concepto (Catálogo Maestro) *</label>
              <ConceptSelect
                kind="obligacion"
                value={form.concept_id}
                legacyLabel={!form.concept_id ? form.name : ""}
                onChange={(id, obj) => setForm({ ...form, concept_id: id, name: obj?.name || (id ? form.name : form.name) })}
                testId="financing-concept-select"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Pertenece a</label>
              <MemberSelect value={form.member_id} onChange={(id) => setForm({ ...form, member_id: id })} preselectCurrent={!editId} testId="financing-member-select" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Entidad financiera / Proveedor</label>
              <ProviderSelect
                testId="financing-provider-select"
                value={form.provider_id}
                onChange={(id, obj) => setForm({ ...form, provider_id: id, entity: obj?.name || form.entity })}
                legacyName={!form.provider_id ? form.entity : ""}
                placeholder="Selecciona una entidad del catálogo…"
                group="Entidades financieras"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Fecha de inicio</label>
              <Input data-testid="financing-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>

            {/* Deuda tradicional o leasing/renting */}
            {(g === "debt" || g === "lease") && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <LabelHint label="Importe original (€)" hint="Capital total financiado al inicio de la operación (lo que te prestaron)." />
                    <Input data-testid="financing-amount-input" inputMode="decimal" value={form.original_amount} onChange={(e) => setForm({ ...form, original_amount: e.target.value })} />
                  </div>
                  <div>
                    <LabelHint label="Interés % anual (TIN)" hint="Tipo de interés nominal anual pactado. Sirve para calcular la liquidación anticipada." />
                    <Input data-testid="financing-rate-input" inputMode="decimal" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} />
                  </div>
                  <div>
                    <LabelHint label="Nº de cuotas" hint="Número total de mensualidades del contrato. Ej: 60 para 5 años." />
                    <Input data-testid="financing-installments-input" inputMode="numeric" value={form.num_installments} onChange={(e) => setForm({ ...form, num_installments: e.target.value.replace(/\D/g, "") })} />
                  </div>
                  <div>
                    <LabelHint label="Cuota mensual (€)" hint="Importe fijo que pagas cada mes. Se usa para calcular la deuda pendiente y el flujo de caja." />
                    <Input data-testid="financing-payment-input" inputMode="decimal" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} />
                  </div>
                </div>
              </>
            )}

            {/* Campos específicos LEASE */}
            {g === "lease" && (
              <div className="rounded-md border border-border p-3 space-y-3" data-testid="lease-fields">
                <div className="text-xs font-semibold text-muted-foreground">Activo asociado</div>
                <div>
                  <LabelHint label="Valor actual estimado del activo (€)" hint="Cuánto valdría el bien HOY si lo vendieras o lo devolvieras (precio de mercado). Ej: Nissan Leaf de 30.000€ que hoy vale 18.000€ → escribe 18000." testid="hint-asset-current" />
                  <Input data-testid="asset-current-value" inputMode="decimal" value={form.asset_current_value} onChange={(e) => setForm({ ...form, asset_current_value: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <LabelHint label="Valor residual (€)" hint="Importe que tendrías que pagar al FINAL del contrato para quedarte con el bien (opción de compra). En renting puro o alquiler flexible normalmente es 0." testid="hint-residual" />
                    <Input data-testid="residual-value" inputMode="decimal" value={form.residual_value} onChange={(e) => setForm({ ...form, residual_value: e.target.value })} />
                  </div>
                  <div>
                    <LabelHint label="Coste de salida (€)" hint="Penalización o gastos por CANCELAR antes de tiempo o devolver el bien con daños/km extra. Si no aplica, escribe 0." testid="hint-exit-cost" />
                    <Input data-testid="exit-cost" inputMode="decimal" value={form.exit_cost} onChange={(e) => setForm({ ...form, exit_cost: e.target.value })} />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="add-to-patrimonio-label">
                  <Checkbox data-testid="add-to-patrimonio" checked={!!form.add_to_patrimonio} onCheckedChange={(v) => setForm({ ...form, add_to_patrimonio: !!v })} />
                  <span className="flex items-center gap-1">
                    Añadir el activo al Patrimonio (visible en Activos)
                    <Popover>
                      <PopoverTrigger asChild><button type="button" className="text-muted-foreground hover:text-foreground"><Info size={12} /></button></PopoverTrigger>
                      <PopoverContent side="top" align="start" className="text-xs w-64 leading-relaxed">
                        Si lo marcas, el bien aparece como activo en tu módulo Patrimonio con su valor actual. Tu patrimonio bruto sube y la vista práctica muestra el "exceso favorable" cuando el activo vale más que la deuda pendiente.
                      </PopoverContent>
                    </Popover>
                  </span>
                </label>
              </div>
            )}

            {/* Vincular activo existente — para deuda tradicional (hipoteca / préstamo / crédito / …) */}
            {g === "debt" && accounts.filter((a) => (a.type || "") !== "Cuenta corriente").length > 0 && (
              <div className="rounded-md border border-border p-3 space-y-2" data-testid="linked-asset-section">
                <div className="flex items-center gap-1">
                  <LabelHint label="Activo asociado (opcional)" hint="Vincula esta obligación con un activo ya registrado (ej. la vivienda hipotecada o el coche financiado). Se mostrará la 'posición neta' = valor del activo − liquidación actual, para que veas la exposición real." testid="hint-linked-asset" />
                </div>
                <Select value={form.linked_asset_id || "__none__"} onValueChange={(v) => setForm({ ...form, linked_asset_id: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid="linked-asset-select"><SelectValue placeholder="Sin activo asociado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin activo asociado</SelectItem>
                    {accounts.filter((a) => (a.type || "") !== "Cuenta corriente").map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name} · {a.type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Campos específicos SUSCRIPCIÓN */}
            {g === "subscription" && (
              <div className="rounded-md border border-border p-3 space-y-3" data-testid="subscription-fields">
                <div>
                  <LabelHint label="Cuota mensual (€)" hint="Importe fijo mensual del alquiler/suscripción. En Grover, por ejemplo, sería la cuota que te cobran cada mes." />
                  <Input data-testid="financing-payment-input" inputMode="decimal" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} />
                </div>
                <div>
                  <LabelHint label="Fecha fin (opcional)" hint="Si el contrato tiene una fecha límite conocida (ej: 24 meses en Grover), indícala. Si es indefinido, déjalo vacío." />
                  <Input data-testid="subscription-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div>
                  <LabelHint label="Total ya pagado (opcional)" hint="Si dejas este campo vacío se calcula automáticamente (cuota × meses transcurridos). Rellénalo si hubo meses gratis, promociones o cambios de precio." />
                  <Input data-testid="total-paid-manual" inputMode="decimal" value={form.total_paid_manual} onChange={(e) => setForm({ ...form, total_paid_manual: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox data-testid="can-return" checked={!!form.can_return} onCheckedChange={(v) => setForm({ ...form, can_return: !!v })} />
                  <span className="flex items-center gap-1">
                    Se puede devolver sin penalización
                    <Popover><PopoverTrigger asChild><button type="button" className="text-muted-foreground hover:text-foreground"><Info size={12} /></button></PopoverTrigger>
                      <PopoverContent side="top" align="start" className="text-xs w-64 leading-relaxed">Marca esta casilla si puedes cancelar el contrato y devolver el bien sin coste extra. Se muestra en la vista práctica.</PopoverContent>
                    </Popover>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox data-testid="has-buy-option" checked={!!form.has_buy_option} onCheckedChange={(v) => setForm({ ...form, has_buy_option: !!v })} />
                  <span className="flex items-center gap-1">
                    Tiene opción de compra
                    <Popover><PopoverTrigger asChild><button type="button" className="text-muted-foreground hover:text-foreground"><Info size={12} /></button></PopoverTrigger>
                      <PopoverContent side="top" align="start" className="text-xs w-64 leading-relaxed">Algunas suscripciones (ej. Grover) permiten comprar el bien tras un número de meses aplicando un descuento sobre lo pagado. Marca la casilla y añade el precio final.</PopoverContent>
                    </Popover>
                  </span>
                </label>
                {form.has_buy_option && (
                  <div>
                    <LabelHint label="Precio opción de compra (€)" hint="Importe que tendrías que pagar HOY (o al final del período) para quedarte con el bien de forma definitiva." />
                    <Input data-testid="buy-option-price" inputMode="decimal" value={form.buy_option_price} onChange={(e) => setForm({ ...form, buy_option_price: e.target.value })} />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="financing-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="finalizado">Finalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <MoneyMovementFields direction="out" values={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix="financing" />

            {/* Datos avanzados sólo para deuda tradicional */}
            {(g === "debt" || g === "lease") && (
              <>
                <button data-testid="financing-adv-toggle" onClick={() => setAdv(!adv)} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
                  {adv ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Datos avanzados (TAE, comisiones, demora…)
                </button>
                {adv && (
                  <div className="space-y-3 border-t border-border pt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Tipo deudor</label>
                        <Select value={form.rate_type} onValueChange={(v) => setForm({ ...form, rate_type: v })}>
                          <SelectTrigger data-testid="financing-ratetype-select"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Fijo">Fijo</SelectItem><SelectItem value="Variable">Variable</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">TAE (%)</label>
                        <Input data-testid="financing-tae-input" inputMode="decimal" value={form.tae} onChange={(e) => setForm({ ...form, tae: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Comisión apertura (€)</label>
                        <Input data-testid="financing-openingfee-input" inputMode="decimal" value={form.opening_fee} onChange={(e) => setForm({ ...form, opening_fee: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Otros gastos (€)</label>
                        <Input data-testid="financing-manualcost-input" inputMode="decimal" value={form.manual_cost} onChange={(e) => setForm({ ...form, manual_cost: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Compensación anticipada (%)</label>
                        <Input data-testid="financing-earlypct-input" inputMode="decimal" value={form.early_repayment_pct} onChange={(e) => setForm({ ...form, early_repayment_pct: e.target.value })} />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">% reducido</label>
                        <Input data-testid="financing-earlyreduced-input" inputMode="decimal" value={form.early_reduced_pct} onChange={(e) => setForm({ ...form, early_reduced_pct: e.target.value })} />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <Button
              data-testid="financing-submit-button"
              onClick={submit}
              disabled={!form.name || !form.start_date || (g !== "subscription" && (!form.num_installments || form.monthly_payment === "")) || (g === "subscription" && form.monthly_payment === "")}
              className="w-full"
            >
              {editId ? "Guardar cambios" : "Crear obligación"}
            </Button>
            {editId && <MovementTrace doc={editDoc} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
